import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  Tooltip,
  Stack,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TableSortLabel,
  TablePagination,
  Menu,
  FormControlLabel,
  Checkbox,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Dialog,
  DialogContent,
  DialogActions,
  Divider,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import CloseIcon from "@mui/icons-material/Close";
import BookmarksOutlinedIcon from "@mui/icons-material/BookmarksOutlined";
import { toast, ToastContainer } from "react-toastify";
import DeleteConfirmationDialog from "../DeleteConfirmationDialog";
import { BUTTON_STYLES } from "../../styles/componentStyles";
import {
  listTopics,
  listAllSubjects,
  listAllChapters,
  createTopic,
  updateTopic,
  previewDelete,
  confirmDelete,
  apiErrorMessage,
} from "../../api/assessmentHierarchy";
import useDebouncedValue from "./useDebouncedValue";
import AssessmentTableSkeleton from "./AssessmentTableSkeleton";
import AssessmentEmptyState from "./shell/AssessmentEmptyState";
import BulkImportButton from "./BulkImportButton";
const ACCENT = "#17479e";

const allColumns = [
  { key: "id", label: "ID", cellCss: { minWidth: "60px", width: "60px" }, getValue: (r) => r.id },
  { key: "title", label: "Name", cellCss: { minWidth: "220px" }, getValue: (r) => r.name },
  { key: "subject", label: "Subject", cellCss: { minWidth: "140px" }, getValue: (r) => r.subjectNames.join(", ") },
  { key: "chapter", label: "Chapter", cellCss: { minWidth: "180px" }, getValue: (r) => r.chapterNames.join(", ") },
  { key: "subTopics", label: "Sub-Topics", cellCss: { align: "center", minWidth: "100px" }, getValue: (r) => r.subTopicCount },
  { key: "questions", label: "Questions", cellCss: { align: "center", minWidth: "110px" }, getValue: (r) => r.questionCount },
  { key: "status", label: "Status", cellCss: { align: "center", minWidth: "120px" }, getValue: (r) => r.status },
];

const defaultVisibleKeys = new Set(allColumns.map((c) => c.key));
const STORAGE_KEY = "assessmentTopicsVisibleColumns";

const statusCfg = {
  ACTIVE: { label: "Active", color: "#2e7d32", bg: "#e8f5e9" },
  INACTIVE: { label: "Inactive", color: "#555", bg: "#f0f0f0" },
};

function StatusChip({ value }) {
  const cfg = statusCfg[value] ?? { label: value ?? "—", color: "#555", bg: "#f5f5f5" };
  return (
    <Chip
      label={cfg.label}
      size="small"
      variant="outlined"
      sx={{ fontWeight: 600, fontSize: "0.7rem", color: cfg.color, borderColor: cfg.color, backgroundColor: cfg.bg }}
    />
  );
}

function TopicFormDialog({ open, topic, subjects, chapters, onClose, onSave }) {
  const [name, setName] = useState("");
  const [nameTouched, setNameTouched] = useState(false);
  const [description, setDescription] = useState("");
  const [subjectIds, setSubjectIds] = useState([]);
  const [chapterIds, setChapterIds] = useState([]);
  const [displayOrder, setDisplayOrder] = useState("");
  const [status, setStatus] = useState("ACTIVE");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setSubmitting(false);
      return;
    }
    setNameTouched(false);
    setName(topic?.name || "");
    setDescription(topic?.description || "");
    setSubjectIds(topic?.subjectIds || []);
    setChapterIds(topic?.chapterIds || []);
    setDisplayOrder("");
    setStatus(topic?.status || "ACTIVE");
  }, [open, topic]);

  const chapterOptions = useMemo(
    () => chapters.filter((c) => c.subjectIds.some((sid) => subjectIds.includes(sid))),
    [chapters, subjectIds],
  );

  const handleSubjectsChange = (value) => {
    setSubjectIds(value);
    // Backend requires every chapter to belong to at least one chosen subject.
    setChapterIds((prev) =>
      prev.filter((cid) => chapters.some((c) => c.id === cid && c.subjectIds.some((sid) => value.includes(sid)))),
    );
  };

  const canSubmit = name.trim() && subjectIds.length > 0 && chapterIds.length > 0;

  const handleSubmit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    try {
      const payload = {
        id: topic?.id,
        name: name.trim(),
        description: description.trim(),
        subjectIds,
        chapterIds,
        active: status === "ACTIVE",
      };
      if (displayOrder !== "") payload.displayOrder = Number(displayOrder);
      await onSave(payload);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{ sx: { borderRadius: 3, backgroundColor: "#f7f8fb", overflow: "hidden" } }}
    >
      <Box
        sx={{
          position: "relative",
          p: 2.5,
          background: `linear-gradient(135deg, ${ACCENT} 0%, #1565c0 100%)`,
          color: "#fff",
        }}
      >
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ position: "absolute", top: 10, right: 10, color: "rgba(255,255,255,0.85)" }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 40,
              height: 40,
              flexShrink: 0,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255,255,255,0.18)",
            }}
          >
            <BookmarksOutlinedIcon sx={{ fontSize: 20 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 800, fontSize: "1.05rem" }}>
              {topic ? "Edit Topic" : "Add Topic"}
            </Typography>
            <Typography sx={{ fontSize: "0.78rem", opacity: 0.85 }}>
              {topic ? "Update name, subjects, chapters and status" : "Name it, link subjects and chapters, set status"}
            </Typography>
          </Box>
        </Stack>
      </Box>
      <DialogContent sx={{ backgroundColor: "#f7f8fb", pt: 3 }}>
        <Card sx={{ boxShadow: 1, borderRadius: 3, overflow: "hidden" }}>
          <CardContent sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2.25 }}>
            <TextField
              label="Topic Name"
              fullWidth
              size="small"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              onBlur={() => setNameTouched(true)}
              error={nameTouched && !name.trim()}
              helperText={nameTouched && !name.trim() ? "Please fill out this field" : undefined}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
            <TextField
              label="Description"
              fullWidth
              size="small"
              multiline
              minRows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
            <FormControl fullWidth size="small" required>
              <InputLabel>Subjects</InputLabel>
              <Select
                multiple
                label="Subjects *"
                value={subjectIds}
                onChange={(e) => handleSubjectsChange(e.target.value)}
                renderValue={(selected) =>
                  selected.map((id) => subjects.find((s) => s.id === id)?.name || id).join(", ")
                }
              >
                {subjects.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    <Checkbox checked={subjectIds.includes(s.id)} size="small" />
                    {s.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small" disabled={subjectIds.length === 0} required>
              <InputLabel>Chapters</InputLabel>
              <Select
                multiple
                label="Chapters *"
                value={chapterIds}
                onChange={(e) => setChapterIds(e.target.value)}
                renderValue={(selected) =>
                  selected.map((id) => chapters.find((c) => c.id === id)?.name || id).join(", ")
                }
              >
                {chapterOptions.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    <Checkbox checked={chapterIds.includes(c.id)} size="small" />
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Display Order (optional)"
              type="number"
              fullWidth
              size="small"
              value={displayOrder}
              onChange={(e) => setDisplayOrder(e.target.value)}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                p: 1.5,
                borderRadius: 2,
                border: "1px solid",
                borderColor: status === "ACTIVE" ? "#2e7d32" : "divider",
                backgroundColor: status === "ACTIVE" ? "#eaf7ec" : "#f5f7fb",
              }}
            >
              <Box>
                <Typography sx={{ fontWeight: 600, fontSize: "0.85rem", color: status === "ACTIVE" ? "#2e7d32" : "#555" }}>
                  {status === "ACTIVE" ? "Active" : "Inactive"}
                </Typography>
                <Typography sx={{ fontSize: "0.7rem", color: "text.secondary" }}>
                  {status === "ACTIVE" ? "Visible to students" : "Hidden from students"}
                </Typography>
              </Box>
              <Switch
                checked={status === "ACTIVE"}
                onChange={(e) => setStatus(e.target.checked ? "ACTIVE" : "INACTIVE")}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": { color: "#2e7d32" },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#2e7d32" },
                }}
              />
            </Box>
          </CardContent>
        </Card>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 2, backgroundColor: "#f7f8fb" }}>
        <Box sx={{ display: "flex", width: "100%", gap: 1.5 }}>
          <Button
            onClick={onClose}
            disabled={submitting}
            sx={{
              flex: "0 0 auto",
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
              color: "#555",
              backgroundColor: "#eceff1",
              "&:hover": { backgroundColor: "#e0e3e6" },
            }}
          >
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            disableElevation
            loading={submitting}
            onClick={handleSubmit}
            disabled={!canSubmit}
            sx={{
              flex: 1,
              textTransform: "none",
              fontWeight: 700,
              borderRadius: 2,
              background: `linear-gradient(135deg, ${ACCENT} 0%, #1565c0 100%)`,
              "&:hover": { background: `linear-gradient(135deg, #123a80 0%, #0d47a1 100%)` },
              "&.Mui-disabled": { background: "#b0bec5" },
            }}
          >
            {topic ? "Save Changes" : "Create Topic"}
          </LoadingButton>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

function AssessmentTopics() {
  const [topics, setTopics] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [subjects, setSubjects] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search);
  const [subjectFilter, setSubjectFilter] = useState("");
  const [chapterFilter, setChapterFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [orderBy, setOrderBy] = useState("id");
  const [order, setOrder] = useState("asc");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deletePreview, setDeletePreview] = useState(null);

  const [visibleColumns, setVisibleColumns] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr)) {
          const validKeys = new Set(allColumns.map((c) => c.key));
          const filtered = arr.filter((k) => validKeys.has(k));
          if (filtered.length > 0) return new Set(filtered);
        }
      }
    } catch {}
    return new Set(Array.from(defaultVisibleKeys));
  });
  const [columnsMenuAnchor, setColumnsMenuAnchor] = useState(null);
  const openColumnsMenu = Boolean(columnsMenuAnchor);

  const loadTopics = () => {
    setLoading(true);
    listTopics({
      page,
      size: rowsPerPage,
      subjectId: subjectFilter,
      chapterId: chapterFilter,
      isActive: statusFilter ? statusFilter === "ACTIVE" : undefined,
      search: debouncedSearch,
    })
      .then((data) => {
        setTopics(data.content);
        setTotalElements(data.totalElements ?? data.content.length);
      })
      .catch((err) => {
        setTopics([]);
        setTotalElements(0);
        toast.error(apiErrorMessage(err, "Failed to load topics"));
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadTopics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, debouncedSearch, subjectFilter, chapterFilter, statusFilter]);

  useEffect(() => {
    Promise.all([listAllSubjects(), listAllChapters()])
      .then(([subjectRows, chapterRows]) => {
        setSubjects(subjectRows);
        setChapters(chapterRows);
      })
      .catch((err) => toast.error(apiErrorMessage(err, "Failed to load subjects/chapters")));
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(visibleColumns)));
    } catch {}
  }, [visibleColumns]);

  const chapterFilterOptions = useMemo(
    () => chapters.filter((c) => !subjectFilter || c.subjectIds.includes(subjectFilter)),
    [chapters, subjectFilter],
  );

  const handleSubjectFilterChange = (value) => {
    setSubjectFilter(value);
    if (!chapters.some((c) => c.id === chapterFilter && c.subjectIds.includes(value))) {
      setChapterFilter("");
    }
    setPage(0);
  };

  const handleOpenColumnsMenu = (e) => setColumnsMenuAnchor(e.currentTarget);
  const handleCloseColumnsMenu = () => setColumnsMenuAnchor(null);
  const toggleColumn = (key) => {
    setVisibleColumns((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  const visibleColumnDefs = allColumns.filter((c) => visibleColumns.has(c.key));

  // The API returns rows in its own order; sorting applies to the current page.
  const sortedData = useMemo(() => {
    const sortValue = (r) =>
      orderBy === "chapter" ? r.chapterNames.join(", ") : orderBy === "subject" ? r.subjectNames.join(", ") : r[orderBy];
    return [...topics].sort((a, b) => {
      const aValue = sortValue(a);
      const bValue = sortValue(b);
      if (aValue == null) return 1;
      if (bValue == null) return -1;
      if (typeof aValue === "string") {
        return order === "asc" ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      }
      if (typeof aValue === "number") {
        return order === "asc" ? aValue - bValue : bValue - aValue;
      }
      return 0;
    });
  }, [topics, orderBy, order]);

  const handleSort = (columnKey) => {
    const isAsc = orderBy === columnKey && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(columnKey);
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const openAdd = () => {
    setEditing(null);
    setFormOpen(true);
  };
  const openEdit = (topic) => {
    setEditing(topic);
    setFormOpen(true);
  };

  const handleSave = (values) => {
    const { id, ...payload } = values;
    const request = id ? updateTopic(id, payload) : createTopic(payload);
    return request
      .then(() => {
        toast.success(id ? "Topic updated successfully!" : "Topic created successfully!");
        setFormOpen(false);
        loadTopics();
      })
      .catch((err) => {
        toast.error(apiErrorMessage(err, "Failed to save topic"));
      });
  };

  // Step 1 of the two-step delete: the backend describes what would be removed
  // (or rejects it, e.g. still referenced by questions/tests) without deleting.
  const openDelete = (topicId) => {
    previewDelete("topic", topicId)
      .then((preview) => setDeletePreview({ ...preview, id: topicId }))
      .catch((err) => toast.error(apiErrorMessage(err, "This topic cannot be deleted")));
  };

  const handleDelete = () => {
    confirmDelete("topic", deletePreview.id)
      .then(() => {
        toast.success("Topic deleted!");
        setDeletePreview(null);
        if (topics.length === 1 && page > 0) setPage(page - 1);
        else loadTopics();
      })
      .catch((err) => {
        setDeletePreview(null);
        toast.error(apiErrorMessage(err, "Failed to delete topic"));
      });
  };

  return (
    <Box sx={{ textAlign: "left" }}>
      <ToastContainer />

      <Card sx={{ boxShadow: 1, mb: 2.5 }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", flex: "1 1 620px", alignItems: "center" }}>
              <TextField
                size="small"
                placeholder="Search topics"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" sx={{ color: "#999" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 220 }}
              />
              <FormControl size="small" sx={{ minWidth: 170 }}>
                <InputLabel>Subject</InputLabel>
                <Select label="Subject" value={subjectFilter} onChange={(e) => handleSubjectFilterChange(e.target.value)}>
                  <MenuItem value="">All subjects</MenuItem>
                  {subjects.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 190 }}>
                <InputLabel>Chapter</InputLabel>
                <Select label="Chapter" value={chapterFilter} onChange={(e) => setChapterFilter(e.target.value)}>
                  <MenuItem value="">All chapters</MenuItem>
                  {chapterFilterOptions.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Status</InputLabel>
                <Select label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="ACTIVE">Active</MenuItem>
                  <MenuItem value="INACTIVE">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
              <Button
                variant="outlined"
                startIcon={<ViewColumnIcon />}
                onClick={handleOpenColumnsMenu}
                sx={{ minWidth: { xs: "120px", md: "auto" }, fontSize: { xs: "10px", md: "12px" } }}
              >
                Columns
              </Button>
              <Menu
                anchorEl={columnsMenuAnchor}
                open={openColumnsMenu}
                onClose={handleCloseColumnsMenu}
                PaperProps={{ sx: { width: 200, maxHeight: 500, overflowY: "auto", borderRadius: 2 } }}
              >
                {allColumns.map((col) => (
                  <FormControlLabel
                    key={col.key}
                    control={<Checkbox checked={visibleColumns.has(col.key)} onChange={() => toggleColumn(col.key)} />}
                    label={col.label}
                    sx={{ pl: 1, pr: 2 }}
                  />
                ))}
              </Menu>
              <BulkImportButton onImported={loadTopics} />
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={openAdd}
                {...BUTTON_STYLES.primary}
                sx={{ minWidth: { xs: "120px", md: "auto" }, fontSize: { xs: "10px", md: "12px" } }}
              >
                Add Topic
              </Button>
            </Stack>
          </Box>
        </CardContent>
      </Card>

      <Paper sx={{ width: "100%", overflow: "hidden", boxShadow: 2, borderRadius: 3 }}>
        <TableContainer sx={{ maxHeight: "calc(100vh - 300px)", minHeight: 420 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {visibleColumnDefs.map((c) => (
                  <TableCell
                    key={c.key}
                    align={c.cellCss?.align}
                    sx={{
                      ...c.cellCss,
                      padding: "10px 12px",
                      backgroundColor: ACCENT,
                      color: "white",
                      fontWeight: 600,
                      position: "sticky",
                      top: 0,
                      zIndex: 3,
                    }}
                    sortDirection={orderBy === c.key ? order : false}
                  >
                    <TableSortLabel
                      active={orderBy === c.key}
                      direction={orderBy === c.key ? order : "asc"}
                      onClick={() => handleSort(c.key)}
                      sx={{
                        color: "#ffffff !important",
                        "&.Mui-active": { color: "#ffffff !important" },
                        "& .MuiTableSortLabel-icon": { color: "#ffffff !important" },
                      }}
                    >
                      {c.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
                <TableCell
                  align="center"
                  sx={{
                    minWidth: "110px",
                    padding: "10px 12px",
                    backgroundColor: ACCENT,
                    color: "white",
                    fontWeight: 600,
                    position: "sticky",
                    top: 0,
                    right: 0,
                    zIndex: 4,
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <AssessmentTableSkeleton columns={visibleColumnDefs.length + 1} />
              ) : sortedData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={visibleColumnDefs.length + 1} sx={{ textAlign: "center", py: 4 }}>
                    <AssessmentEmptyState message="No topics found" hint="Try changing your search or filters." />
                  </TableCell>
                </TableRow>
              ) : (
                sortedData.map((row) => (
                  <TableRow
                    key={row.id}
                    hover
                    sx={{ "&:nth-of-type(odd)": { backgroundColor: "rgba(0,0,0,0.02)" } }}
                  >
                    {visibleColumnDefs.map((c) => (
                      <TableCell key={c.key} align={c.cellCss?.align} sx={{ padding: "10px 12px", ...c.cellCss }}>
                        {(() => {
                          if (c.key === "id") {
                            return (
                              <Typography sx={{ fontSize: "0.8rem", color: "text.secondary" }}>{row.id}</Typography>
                            );
                          }
                          if (c.key === "title") {
                            return <Typography sx={{ fontWeight: 600, fontSize: "0.85rem" }}>{row.title}</Typography>;
                          }
                          if (c.key === "status") return <StatusChip value={row.status} />;
                          if (c.key === "subject") return row.subjectNames.join(", ") || "—";
                          if (c.key === "chapter") return row.chapterNames.join(", ") || "—";
                          return c.getValue(row);
                        })()}
                      </TableCell>
                    ))}
                    <TableCell
                      align="center"
                      sx={{ padding: "10px 12px", position: "sticky", right: 0, backgroundColor: "#fff", zIndex: 1 }}
                    >
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Tooltip title="Edit Topic" arrow placement="top">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => openEdit(row)}
                            sx={{ "&:hover": { backgroundColor: "primary.light", color: "white" } }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Topic" arrow placement="top">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => openDelete(row.id)}
                            sx={{ "&:hover": { backgroundColor: "error.light", color: "white" } }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={totalElements}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            borderTop: "1px solid",
            borderColor: "divider",
            "& .MuiTablePagination-toolbar": { paddingLeft: 2, paddingRight: 2 },
          }}
        />
      </Paper>

      <TopicFormDialog
        open={formOpen}
        topic={editing}
        subjects={subjects}
        chapters={chapters}
        onClose={() => setFormOpen(false)}
        onSave={handleSave}
      />

      <DeleteConfirmationDialog
        open={Boolean(deletePreview)}
        onClose={() => setDeletePreview(null)}
        onConfirm={handleDelete}
        title="Confirm Delete"
        message={deletePreview?.message || "This action cannot be undone."}
        confirmText="DELETE"
        confirmButtonText="Delete"
        cancelButtonText="Cancel"
      />
    </Box>
  );
}

export default AssessmentTopics;
